'use strict';

import { cn_image_dir } from "../utils/image_dir";

//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2025 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Constants
//***********************************************************************************
//***********************************************************************************

/** Facing supports */
export const CN_FACING_SUPPORTS = {
    FLOOR: "floor",
    CEILING: "ceiling",
    WALL: "wall",
    FACADE: "facade",
    EXTERIOR: "exterior",
    OPENING: "opening",
    NICHE: "niche",
    STAIR: "stair",
    OBJECT: "object",
    BEAM: "beam",
    COLUMN: "column",
    PIPE: "pipe",
    MOLDING: "molding"
};

/** Constant class */
export class cn_constant {
    /**
     * Constructor
     * @param {string} label
     * @param {string} code
     * @param {number[] | object} args
     */
    constructor(label, code, args) {
        this.code = code;
        this.label = label;
        this.color = [255, 255, 255, 1];
        this.url = "";
        if (args) {
            let color = [255, 255, 255, 1];
            if (Array.isArray(args))
            {
                color = args;
            }
            else {
                if (typeof(args.color) == 'object')
                    color = args.color;
                if (typeof(args.url) == 'string')
                    this.url = args.url;
            }

            this.color = color.concat([]);
            if (color.length == 0) color = [256, 256, 256, 1];
            while (color.length < 3) color.push(color[0]);
            if (color.length < 4) color.push(1);
        }
    }

    /**
     * Returns label from code, or 'false'.
     * @param {string} code
     * @param {cn_constant[]} constants
     * @returns {string | boolean}
     */
    static code_to_label(code, constants) {
        let cc = constants.find(c => c.code == code);
        return cc ? cc.label : false;
    }

    /**
     * Returns color from code, or 'false'.
     * Color is [0-256,0-256,0-256,0-1]
     * @param {string} code
     * @param {cn_constant[]} constants
     * @returns {number[] | boolean}
     */
    static code_to_color(code, constants) {
        let cc = constants.find(c => c.code == code);
        return cc ? cc.color : false;
        return false;
    }

    /**
     * Returns color string
     * @param {string} code
     * @param {cn_constant[]} constants
     * @returns {string}
     */
    static code_to_rgbcolor(code, constants) {
        const color = cn_constant.code_to_color(code, constants);
        if (!color) return 'rgb(0,0,0)';
        return `rgb(${color[0]},${color[1]},${color[2]})`;
    }

    /**
     * Returns float color from code, or 'false'.
     * Color is [0-1,0-1,0-1,0-1]
     * @param {string} code
     * @param {cn_constant[]} constants
     * @returns {number[]}
     */
    static code_to_float_color(code, constants) {
        const color = cn_constant.code_to_color(code, constants);
        if (!color) return [0, 0, 0, 1];
        return [color[0] / 255, color[1] / 255, color[2] / 255, color[3]];
    }
}

//***********************************************************************************
//**** Opening Constants
//***********************************************************************************

export const CN_OPENING_FACING_MAIN = 0;
export const CN_OPENING_FACING_SASH = 0;
export const CN_OPENING_FACING_OPENING = 1;
export const CN_OPENING_FACING_SILL = 2;
export const CN_OPENING_FACING_TRANSOM = 3;
export const CN_OPENING_FACING_CLOSING = 4;

export var FRAME_MATERIALS = [
    new cn_constant('Aluminium', 'alu', [100, 100, 100, 1]),
    new cn_constant('PVC', 'pvc', [200, 200, 200, 1]),
    new cn_constant('Bois', 'wood', [150, 125, 100, 1]),
    new cn_constant('Acier', 'steel', [70, 70, 70, 1])
];

export var CLOSING_MATERIALS = [
    new cn_constant('Aluminium', 'alu', [100, 100, 100, 1]),
    new cn_constant('PVC', 'pvc', [200, 200, 200, 1]),
    new cn_constant('Bois', 'wood', [150, 125, 100, 1]),
    new cn_constant('Acier', 'steel', [70, 70, 70, 1]),
];

export const GLAZING_LIST = [
    new cn_constant('Aucun', 'none', [0, 0, 0, 1]),
    new cn_constant('Simple', 'single', [250, 250, 255, 0.2]),
    new cn_constant('Double', 'double', [220, 220, 255, 0.3]),
    new cn_constant('Triple', 'triple', [200, 200, 255, 0.4])
];

//***********************************************************************************
//**** Stairs Constants
//***********************************************************************************

/** Pipe extra materials */
export const STAIRS_PARTS = [
    new cn_constant('Marche', 'tread'),
    new cn_constant('Contremarche', 'riser'),
    new cn_constant('Limon', 'stair_stringer'),
    new cn_constant('Crémaillère', 'cut_stringer'),
    new cn_constant('Garde-corps', 'railing'),
    new cn_constant('Main courante', 'handrail'),
];

export var STAIRS_MATERIALS = {
    'tread': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
    'riser': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
    'stair_stringer': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
    'cut_stringer': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
    'railing': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
    'handrail': [
        new cn_constant('Aucun', 'none'),
        new cn_constant('Bois', 'wood'),
        new cn_constant('Béton', 'concrete'),
        new cn_constant('Acier', 'steel'),
        new cn_constant('Aluminium', 'alu'),
        new cn_constant('Autre', 'other')
    ],
};

/**
 * Set stairs materials for a given code
 * @param {string} code
 * @param {Array<{code: string, label: string}>} materials
 * @returns
 */
export function cn_set_stairs_materials(code, materials) {
    if (typeof (STAIRS_MATERIALS[code]) != 'object') return;

    STAIRS_MATERIALS[code] = [];
    materials.forEach(m => STAIRS_MATERIALS[code].push(new cn_constant(m.label, m.code)));
}

//***********************************************************************************
//**** Pipe Constants
//***********************************************************************************

/** Pipe extra materials */
export const PIPE_FLUID_LIST = [
    new cn_constant('Eau', 'water', [0, 0, 255]),
    new cn_constant('Eaux usagées', 'waste', [12, 114, 92]),
    new cn_constant('Air', 'air', [71, 181, 255]),
    new cn_constant('Gaz', 'gaz', [138, 138, 138]),
    new cn_constant('Autre', 'other'),
];

export const PIPE_MATERIAL_LIST = [
    new cn_constant('PVC', 'pvc', [100, 100, 100]),
    new cn_constant('Cuivre', 'copper', [150, 100, 100]),
    new cn_constant('PER', 'per', [100, 100, 150]),
    new cn_constant('Plomb', 'lead', [30, 30, 30]),
    new cn_constant('Fibre-ciment', 'fibercement', [200, 200, 200]),
    new cn_constant('Acier', 'steel', [70, 70, 70]),
    new cn_constant('Fonte', 'font', [80, 30, 30]),
    new cn_constant('Autre', 'other', [100, 150, 100]),
];

export var PIPE_EXTRA_MATERIAL_LIST = [];
/** For test :
[
    new cn_constant('Aaa', 'aaa'),
    new cn_constant('Bbbb', 'abbb')
];*/

export const PIPE_SHAPE_LIST = [
    new cn_constant('Circulaire', 'circular'),
    new cn_constant('Rectangulaire', 'rectangular'),
];

/** Niche shapes */
export const NICHE_SHAPE_LIST = [
    new cn_constant('Rectangulaire', 'rectangle'),
    new cn_constant('Arrondi', 'round'),
    new cn_constant('Triangulaire', 'triangle'),
];

//***********************************************************************************
//**** Layer constants
//***********************************************************************************

export const DASHARRAY_LIST = [
    "0",
    "5, 5",
    "15, 5",
    "15, 5, 5, 5"
];

//***********************************************************************************
//**** Patterns
//***********************************************************************************

export const PATTERN_LIST = [
    new cn_constant('full', 'full'),
    new cn_constant('horizontal', 'horizontal'),
    new cn_constant('vertical', 'vertical'),
    new cn_constant('bigdots', 'bigdots'),
    new cn_constant('bricks', 'bricks')
];

/**
 * Returns the pattern url of a given code.
 * @param {string} code 
 * @return {string} 
 */
export function cn_get_pattern_url(code) {
    let pattern = PATTERN_LIST.find(p => p.code == code);
    if (!pattern) return "";
    if (pattern.url != "") return pattern.url;
    return `${cn_image_dir()}pattern_${pattern.code}.png`;
}

/**
 * Returns the pattern url of a given code.
 * @param {string} code 
 * @return {boolean} 
 */
export function cn_add_pattern(code, url) {
    let pattern = PATTERN_LIST.find(p => p.code == code);
    if (pattern) return false;

    PATTERN_LIST.push(new cn_constant(code,code,{url:url}));
    return true;
}

//***********************************************************************************
//**** Global Constants
//***********************************************************************************


export const SPACE_EXTERIOR_LABEL = 'Extérieur';

export const CN_WALL_COSINE_SHAPE_THRESHOLD = Math.cos(10 * Math.PI / 180);
export const CN_WALL_SINE_SHAPE_THRESHOLD = Math.sin(10 * Math.PI / 180);
